import React from 'react';
import { Box, Heading, Text } from '@indeed/ifl-components';
import { useGetJobDescriptionQuery } from './__generated__/JobDescription.generated';

export interface JobDescriptionProps {
    jobKey?: string;
}

const JobDescription: React.FunctionComponent<JobDescriptionProps> = ({
    jobKey = 'job_67890', // Default job key for demo purposes
    // For testing different scenarios, try:
    // - 'job_67890' (active Indeed job)
    // - 'job_12345' (expired TechCorp job)
}) => {
    // Fetch job description data using generated Apollo hook
    const { data, loading, error } = useGetJobDescriptionQuery({
        variables: { jobKeys: [jobKey] },
    });

    if (loading) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    padding: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'border.subtle',
                    blockSize: 'fit-content',
                }}
            >
                <Text>Loading job description...</Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    padding: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'border.subtle',
                    blockSize: 'fit-content',
                }}
            >
                <Text>Error loading job description: {error.message}</Text>
            </Box>
        );
    }

    if (!data?.jobData?.results?.length || !data.jobData.results[0]?.job) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    padding: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'border.subtle',
                    blockSize: 'fit-content',
                }}
            >
                <Text>No job information found.</Text>
            </Box>
        );
    }

    const job = data.jobData.results[0].job;

    return (
        <Box
            sx={{
                backgroundColor: 'background.default',
                padding: 6,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'border.subtle',
                blockSize: 'fit-content',
            }}
        >
            {/* Job Header - Only available OneGraph fields */}
            <Box sx={{ marginBlockEnd: 4 }}>
                <Heading
                    as="h2"
                    level={2}
                    sx={{
                        color: 'foreground.primary',
                        marginBlockEnd: 2,
                    }}
                >
                    {job.title}
                </Heading>
                <Text
                    sx={{
                        color: 'primary.default',
                        fontSize: 3,
                        fontWeight: 'bold',
                        marginBlockEnd: 2,
                    }}
                >
                    {job.employer?.name ?? 'Company name not available'}
                </Text>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBlockEnd: 3 }}>
                    <Text
                        sx={{
                            backgroundColor: 'background.secondary',
                            padding: 2,
                            borderRadius: 1,
                            fontSize: 1,
                        }}
                    >
                        {job.location.formatted.short}
                    </Text>
                    {job.expired && (
                        <Text
                            sx={{
                                backgroundColor: 'warning.subtle',
                                color: 'warning.strong',
                                padding: 2,
                                borderRadius: 1,
                                fontSize: 1,
                            }}
                        >
                            Job Expired
                        </Text>
                    )}
                </Box>
            </Box>

            {/* Job Details - Limited to available data */}
            <Box sx={{ marginBlockEnd: 4 }}>
                <Heading as="h3" level={4} sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}>
                    Job Information
                </Heading>
                <Box
                    sx={{
                        padding: 3,
                        backgroundColor: 'background.secondary',
                        borderRadius: 1,
                    }}
                >
                    <Text sx={{ fontSize: 2, color: 'foreground.secondary', marginBlockEnd: 2 }}>
                        <strong>Location:</strong> {job.location.city ?? 'Unknown city'},{' '}
                        {job.location.countryName ?? 'Unknown country'}
                    </Text>
                    <Text sx={{ fontSize: 1, color: 'foreground.secondary' }}>
                        <strong>Job Key:</strong> {job.key}
                    </Text>
                    {job.expired && (
                        <Text sx={{ fontSize: 1, color: 'warning.strong', marginBlockStart: 2 }}>
                            <strong>Status:</strong> This job posting has expired
                        </Text>
                    )}
                </Box>

                <Box
                    sx={{
                        marginBlockStart: 3,
                        padding: 3,
                        backgroundColor: 'background.tertiary',
                        borderRadius: 1,
                    }}
                >
                    <Text sx={{ fontSize: 1, color: 'foreground.secondary', fontStyle: 'italic' }}>
                        Additional job details like description, requirements, and benefits are not
                        available through the current OneGraph JobRetrievalService. These may be
                        available through other Indeed services or require integration with
                        additional data sources.
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};

export default JobDescription;
